import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { SuggestSingleSelect } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import Papa from 'papaparse';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { buildRateRuleValidationSchema } from './rateRulesBulkUploadValidation';

import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

const uploadRateRulesFile = async ({ files, callback }) => {
    files.forEach((file) => {
        Papa.parse(file, {
            complete: (results) => {
                callback(results.data);
            },
            header: true,
            dynamicTyping: Object.fromEntries(
                Object.keys(buildRateRuleValidationSchema([])).map((key) => [
                    key,
                    key !== 'product_service_id',
                ]),
            ),
            skipEmptyLines: 'greedy',
        });
    });
};

/** Generate csv for rate rules upload */
const generateRateRulesFile = () => {
    const csvHeader = `${Object.keys(buildRateRuleValidationSchema([])).join(',')}\n`; // header row

    const hiddenElement = document.createElement('a');
    hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csvHeader)}`;
    hiddenElement.target = '_blank';
    hiddenElement.download = 'RateUpload.csv';
    hiddenElement.click();
};

export const RateRulesBulkUpload = () => {
    const { payerId } = useParams();
    const navigate = useNavigate();
    const payers = useGet('/reference/payers/');
    const payerOptions = useMemo(
        () => payers?.map((payer) => ({ name: payer.payer_id })),
        [payers],
    );
    const payerSegmentsList = useGet('/reference/payer_segments');
    const segmentsLoaded = payerSegmentsList != null;
    const validSegmentIds = useMemo(
        () =>
            (payerSegmentsList ?? [])
                .filter((segment) => segment.payer_id === payerId)
                .map((segment) => segment.segment_id),
        [payerSegmentsList, payerId],
    );
    const validationSchema = useMemo(
        () =>
            Yup.object({
                client_id: Yup.string().required(validationStrings.requiredField.errorMessage),
                rate_rules_upload: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
                rate_rules: Yup.array(Yup.object(buildRateRuleValidationSchema(validSegmentIds))),
            }),
        [validSegmentIds],
    );
    const [uploadIssues, setUploadIssues] = useState([]);

    return (
        <div className="sds_list-page">
            <div className="sds_list-page__content--top">
                <SuggestSingleSelect
                    id="payerId"
                    name="payerId"
                    label="Payer"
                    isLoading={!payers}
                    values={payerOptions}
                    helperText="Payer to edit the rate rules for"
                    onChange={(newValue) =>
                        navigate(`/rate-rules/bulk-upload/${newValue?.name ?? ''}`, {
                            replace: true,
                        })
                    }
                    selectedItem={payerOptions?.find((payer) => payer.name === payerId)}
                />
            </div>
            {payerId && segmentsLoaded && (
                <Formik
                    initialValues={{
                        client_id: payerId,
                    }}
                    enableReinitialize
                    validateOnMount
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                        try {
                            const rules = values.rate_rules.map((rule) => ({
                                ...rule,
                                effective_start_date:
                                    rule?.effective_start_date != null
                                        ? dayjs(rule.effective_start_date).toISOString()
                                        : null,
                                effective_end_date:
                                    rule?.effective_end_date != null
                                        ? dayjs(rule.effective_end_date).toISOString()
                                        : null,
                            }));

                            const result = await put('/ratesetter/admin/rate_rules/', {
                                rate_rules: rules,
                                payer_id: values.client_id,
                            });

                            setUploadIssues(result?.issues ?? []);
                            SuccessToast({ message: result.message });
                        } catch (err) {
                            handleError('Error updating rate rules.', err);
                        }
                    }}
                >
                    {(formik) => {
                        const showFileErrors =
                            uploadIssues.length > 0 ||
                            (!!formik.touched.rate_rules_upload &&
                                (!!formik.errors.rate_rules_upload || !!formik.errors.rate_rules));
                        const rawErrors = formik.errors.rate_rules ?? [];
                        const fileErrors = showFileErrors
                            ? [
                                  formik.errors.rate_rules_upload,
                                  ...rawErrors.map((error, index) => {
                                      if (error != null) {
                                          const [elem, issue] = Object.entries(error);
                                          const rowKey = `${index}.${elem}.${issue}`;
                                          return (
                                              <div key={rowKey}>
                                                  <div>Row {index + 1}:</div>
                                                  {Object.keys(error).map((key) => (
                                                      <div
                                                          className="padding__left--medium"
                                                          key={key}
                                                      >
                                                          {key}: {error[key]}
                                                      </div>
                                                  ))}
                                              </div>
                                          );
                                      }
                                      return null;
                                  }),
                                  ...[...new Set(uploadIssues)].map((issue) => (
                                      <div key={issue} style={{ whiteSpace: 'pre-wrap' }}>
                                          {issue}
                                      </div>
                                  )),
                              ]
                            : [];
                        return (
                            <>
                                <FullscreenLoading
                                    isShowing={formik.isSubmitting}
                                    loadingText="Submitting rate rules for upload..."
                                />
                                <FormContainer
                                    formTitle="Rate Rules"
                                    formHeaderRightContent={
                                        <ActionButtonText
                                            label="Generate File"
                                            leftIcon={IconNames.IMPORT}
                                            onClick={() => generateRateRulesFile()}
                                        />
                                    }
                                    formLeftContent={
                                        <FileUploadFormik
                                            id="rate_rules_upload"
                                            label="Rate Rules File"
                                            required
                                            maxAllowedFiles={1}
                                            onAddFiles={async (files) => {
                                                await uploadRateRulesFile({
                                                    files,
                                                    callback: async (data) => {
                                                        await formik.setFieldValue(
                                                            'rate_rules_upload',
                                                            files,
                                                        );
                                                        await formik.setFieldValue(
                                                            'rate_rules',
                                                            data,
                                                        );
                                                    },
                                                });
                                            }}
                                            onRemoveFile={async () => {
                                                setUploadIssues([]);
                                                await formik.setFieldValue('rate_rules', undefined);
                                                await formik.setFieldValue(
                                                    'rate_rules_upload',
                                                    undefined,
                                                );
                                            }}
                                            requiredValidationMessage={
                                                showFileErrors ? (
                                                    <div
                                                        style={{
                                                            maxHeight: 300,
                                                            overflowY: 'auto',
                                                        }}
                                                    >
                                                        {fileErrors}
                                                    </div>
                                                ) : undefined
                                            }
                                            accept=".csv"
                                        />
                                    }
                                    primaryButtonLabel="Submit"
                                    onSecondaryButtonClick={() => {
                                        formik.resetForm();
                                        setUploadIssues([]);
                                    }}
                                    onFormSubmit={formik.handleSubmit}
                                    isSubmitting={!formik.isValid || formik.isSubmitting}
                                />
                            </>
                        );
                    }}
                </Formik>
            )}
        </div>
    );
};
